import React, { useState, useEffect } from "react";
import { Grid, Icon, InputLabel, Card, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import DataTable from "examples/Tables/DataTable";
import { useUser } from "context/UserContext";

import { postUploadNoResponse, post, get, getFile, postNoResponse } from "utils/WebService";
import { successAlert, errorAlert } from "utils/Functions";
import ReactMarkdown from "react-markdown";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import { useMaterialUIController } from "context";

export default function ProcessDocuments({ id }) {
    const { tipoDocumentoProcessoEnum } = useUser();
    const [selectedTipo, setSelectedTipo] = useState(null);
    const [file, setFile] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [filterData, setFilterData] = useState("");
    const [filterTipo, setFilterTipo] = useState(null);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    useEffect(() => {
        if (id) {
            get(`/documents/list/${id}`)
                .then(data => {
                    if (data && Array.isArray(data)) {
                        setDocuments(data);
                    }
                })
                .catch(error => {
                    console.error("Erro ao listar documentos do banco:", error);
                });
        }
    }, [id]);


    // AI States
    const [openAiModal, setOpenAiModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiSummary, setAiSummary] = useState("");
    const [documentTarget, setDocumentTarget] = useState(null);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = () => {
        if (!selectedTipo || !file || !id) {
            errorAlert("Selecione o tipo, o arquivo e verifique se o processo está salvo.");
            return;
        }
        
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tipo", selectedTipo.code);
        formData.append("processoId", id);

        postUploadNoResponse("/documents/upload", formData)
            .then(async (response) => {
                if (!response.ok) {
                    const resData = await response.json().catch(() => ({}));
                    throw new Error(resData.error || "Erro ao fazer upload");
                }
                const resData = await response.json();
                
                const newDoc = {
                    id: resData.id,
                    tipo: selectedTipo.code,
                    nomeArquivo: file.name,
                    caminho: resData.path,
                    dataUpload: new Date().toLocaleDateString('pt-BR'),
                    usuarioNome: "Você"
                };
                
                setDocuments([...documents, newDoc]);
                setFile(null);
                setSelectedTipo(null);
                successAlert("Documento enviado com sucesso!");
            })
            .catch((error) => {
                console.error("Upload error:", error);
                errorAlert(error.message || "Erro ao enviar documento. Tente novamente.");
            });
    };

    const handleDownload = (doc) => {
        getFile(`/documents/download/${doc.id}`, doc.nomeArquivo)
            .catch(error => errorAlert("Erro ao fazer download do arquivo."));
    };

    const handleGenerateSummary = (doc) => {
        if (!doc.caminho) {
            errorAlert("Caminho do arquivo não encontrado. Você precisa fazer o upload real do documento primeiro.");
            return;
        }

        setDocumentTarget(doc.nomeArquivo);
        setAiSummary("");
        setIsGenerating(true);
        setOpenAiModal(true);

        post("/ai/document-summary", { documentoId: doc.id.toString() })
            .then(data => {
                setAiSummary(data.summary || "Resumo não retornou conteúdo.");
                setIsGenerating(false);
            })
            .catch(error => {
                console.error("Erro ao comunicar com a IA:", error);
                setAiSummary(`Falha ao gerar o resumo inteligente.\nDetalhe: ${error.message || "Ocorreu um erro no servidor."}`);
                setIsGenerating(false);
            });
    };

    const columns = [
        { 
            header: "Tipo de Documento", 
            accessorKey: "tipo", 
            width: "30%",
            cell: ({ row }) => {
                const val = row.original.tipo;
                const found = tipoDocumentoProcessoEnum?.find(t => t.code == val || t.name == val);
                return found ? found.name : val;
            }
        },
        { header: "Nome do Arquivo", accessorKey: "nomeArquivo", width: "40%" },
        { header: "Data", accessorKey: "dataUpload", width: "10%" },
        { header: "Adicionado Por", accessorKey: "usuarioNome", width: "15%" },
        {
            header: "Ações",
            accessorKey: "acoes",
            width: "15%",
            cell: ({ row }) => (
                <MDBox display="flex" gap={1}>
                    <Tooltip title="Resumo via IA" placement="top">
                        <MDButton variant="text" color="warning" size="small" onClick={() => handleGenerateSummary(row.original)}>
                            <Icon sx={{ color: '#FCAC00' }}>auto_awesome</Icon>
                        </MDButton>
                    </Tooltip>
                    <Tooltip title="Download" placement="top">
                        <MDButton variant="text" color="info" size="small" onClick={() => handleDownload(row.original)}>
                            <Icon>download</Icon>
                        </MDButton>
                    </Tooltip>
                    <Tooltip title="Excluir" placement="top">
                        <MDButton variant="text" color="error" size="small" onClick={() => {
                            if (window.confirm("Deseja realmente excluir este documento?")) {
                                postNoResponse(`/documents/delete/${row.original.id}`)
                                    .then(() => {
                                        successAlert("Excluído com sucesso!");
                                        setDocuments(documents.filter(d => d.id !== row.original.id));
                                    })
                                    .catch(() => errorAlert("Erro ao excluir o documento."));
                            }
                        }}>
                            <Icon>delete</Icon>
                        </MDButton>
                    </Tooltip>
                </MDBox>
            )
        }
    ];

    const filteredDocuments = documents.filter(doc => {
        let matchesTipo = true;
        let matchesData = true;
        if (filterTipo && filterTipo.code) {
            matchesTipo = doc.tipo === filterTipo.code || doc.tipo === filterTipo.name;
        }
        if (filterData) {
            const [d, m, y] = (doc.dataUpload || "").split("/");
            if (d && m && y) {
                const docDateStr = `${y}-${m}-${d}`;
                matchesData = docDateStr === filterData;
            }
        }
        return matchesTipo && matchesData;
    });

    return (
        <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Documentos do Processo
            </MDTypography>
            
            <Card sx={{ p: 2, mb: 3, backgroundColor: "rgba(0,0,0,0.02)", boxShadow: "none", border: "1px solid #eee" }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5 }}>Tipo de Documento</InputLabel>
                        <MDSelect
                            getOptionValue={option => option.code}
                            getOptionLabel={option => option.name}
                            name="tipoDocumento"
                            placeholder="Selecione um tipo..."
                            options={tipoDocumentoProcessoEnum}
                            value={selectedTipo}
                            onChange={(option) => setSelectedTipo(option)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                         <InputLabel sx={{ px: 1, pb: 0.5 }}>Arquivo</InputLabel>
                         <MDBox display="flex" alignItems="center">
                             <MDButton
                                variant="contained"
                                component="label"
                                color="dark"
                                size="small"
                                sx={{ mr: 2, height: "38px" }}
                             >
                                <Icon sx={{ mr: 1 }}>attach_file</Icon> Escolher Arquivo
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                />
                             </MDButton>
                             {file && (
                                <MDTypography variant="caption" color="text" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.name}
                                </MDTypography>
                             )}
                         </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <MDButton 
                            variant="gradient" 
                            color="info" 
                            fullWidth
                            onClick={handleUpload}
                            disabled={!selectedTipo || !file}
                            sx={{ height: "38px" }}
                        >
                            <Icon sx={{ fontWeight: "bold" }}>cloud_upload</Icon>
                            &nbsp;Fazer Upload
                        </MDButton>
                    </Grid>
                </Grid>
            </Card>

            <MDTypography variant="h6" fontWeight="medium" mb={1} fontSize="14px">
                Documentos Anexados
            </MDTypography>

            <Card sx={{ p: 2, mb: 3, backgroundColor: "rgba(0,0,0,0.02)", boxShadow: "none", border: "1px solid #eee" }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5 }}>Filtrar por Tipo</InputLabel>
                        <MDSelect
                            getOptionValue={option => option.code}
                            getOptionLabel={option => option.name}
                            name="filterTipo"
                            placeholder="Todos os tipos..."
                            options={tipoDocumentoProcessoEnum}
                            value={filterTipo}
                            onChange={(option) => setFilterTipo(option)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5 }}>Filtrar por Data</InputLabel>
                        <MDTextFieldCalendar
                            name="filterDataDoc"
                            type="date"
                            value={filterData}
                            fullWidth
                            onChange={(e) => setFilterData(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            darkMode={darkMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MDButton size="medium" 
                            variant="contained" 
                            color="info" 
                            fullWidth
                            onClick={() => {
                                setFilterData("");
                                setFilterTipo(null);
                            }}
                            sx={{ height: "38px" }}
                        >
                            <Icon sx={{ fontWeight: "bold" }}>clear</Icon>
                            &nbsp;Limpar Filtros
                        </MDButton>
                    </Grid>
                </Grid>
            </Card>

            <DataTable
                tableState={{ current: Object() }}
                columnData={columns}
                data={filteredDocuments}
                entriesPerPage={false}
                showTotalEntries={false}
                isSorted={false}
                fetchData={() => {}}
                noEndBorder
            />

            {/* AI Summary Modal */}
            <Dialog 
                open={openAiModal} 
                onClose={() => !isGenerating && setOpenAiModal(false)}
                fullWidth 
                maxWidth="sm"
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ color: '#FCAC00' }}>auto_awesome</Icon>
                    Resumo Inteligente
                </DialogTitle>
                <DialogContent dividers sx={{ minHeight: '150px' }}>
                    {isGenerating ? (
                        <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={2} py={4}>
                            <CircularProgress color="warning" />
                            <MDTypography variant="body2" color="text">
                                A Inteligência Artificial está lendo e interpretando o arquivo <b>{documentTarget}</b>...
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox sx={{ 
                            '& p': { margin: '0 0 10px 0', fontSize: '14px', lineHeight: 1.5, color: '#344767' }, 
                            '& ul': { margin: '0 0 10px 20px', fontSize: '14px', lineHeight: 1.5, color: '#344767' },
                            '& li': { marginBottom: '4px' },
                            '& strong': { fontWeight: 600 }
                        }}>
                            <ReactMarkdown>{aiSummary}</ReactMarkdown>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton 
                        onClick={() => setOpenAiModal(false)} 
                        color="dark" 
                        variant="text"
                        disabled={isGenerating}
                    >
                        Fechar
                    </MDButton>
                    {!isGenerating && (
                        <MDButton 
                            color="info" 
                            variant="gradient"
                            onClick={() => {
                                navigator.clipboard.writeText(aiSummary);
                                alert('Resumo copiado para a área de transferência!');
                            }}
                        >
                            <Icon sx={{ mr: 1 }}>content_copy</Icon> Copiar Resumo
                        </MDButton>
                    )}
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}
